import { ArrowBack as ArrowBackIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, Button, Card, CircularProgress, Grid2 as Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TitlePageLayout } from '@/admins/SmartReports/components/Layouts/TitlePage';
import { useGetReport } from '@/admins/SmartReports/hooks/useGetReports';
import { ADMINS_SMART_REPORTS_PATH } from '@/utils/constants';
import { SmartReportLayouts } from '../Layouts';
import { PageNumber } from '../StaticContent/PageNumber';
import { Watermark } from '../StaticContent/Watermark';
import { WatermarkLogo } from '../StaticContent/WatermarkLogo';

const useStyles = makeStyles(theme => ({
  loader: {
    ...theme.palette.loading,
  },
  root: {
    ...theme.sizes.fullPageNoAppBar,
  },
}));

function printDiv(divId) {
  // Get the div content
  var divContent = document.getElementById(divId).innerHTML;

  // Create a new window
  var printWindow = window.open('', '', 'height=500,width=800');

  // Write the div content to the new window
  printWindow.document.write('<html><head><title>Print Div Content</title>');
  printWindow.document.write('</head><body>');
  printWindow.document.write(divContent);
  printWindow.document.write('</body></html>');

  // Close the document to trigger the print dialog
  printWindow.document.close();

  // Print the content
  printWindow.print();
}

export const ViewPage = ({ entity, pageData }) => {
  const layout = useMemo(
    () => SmartReportLayouts.find(layoutItem => layoutItem.id === pageData?.layout),
    [pageData?.layout],
  );

  // If layout is not found, fallback to the first layout
  const LayoutComponent = layout?.ComponentView || SmartReportLayouts[0].ComponentView;

  // Hide watermark, logo and number on title page
  const showFooter = layout && layout.id !== TitlePageLayout.id;

  return (
    <Card className='smart-report-page' sx={{ aspectRatio: '16 / 9', width: '100%' }}>
      <LayoutComponent entity={entity} pageData={pageData} />
      {showFooter && (
        <>
          <Watermark />
          <WatermarkLogo />
          <PageNumber pageNumber={pageData?.number} />
        </>
      )}
    </Card>
  );
};

export const ViewSmartReport = ({ isPreview }) => {
  const classes = useStyles();
  const { id } = useParams();
  const { report, loading, error } = useGetReport(id);
  const entity = useMemo(
    () =>
      ({
        ...report?.smartReportEntities[0]?.entity,
        type: report?.smartReportEntities[0]?.entityType,
      }) || null,
    [report],
  );

  if (loading) {
    return (
      <Grid
        container
        className={clsx(classes.loader, classes.root)}
        justifyContent='center'
        alignItems='center'
      >
        <CircularProgress color='inherit' size={20} />
      </Grid>
    );
  }

  if (error) return null;

  if (!report || !id) {
    return <div>Report not found</div>;
  }

  return (
    <Box
      padding={3}
      spacing={1}
      sx={{
        maxWidth: '1400px',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '2em',
      }}
    >
      <Grid container justifyContent='space-between' sx={{ margin: '1em 0 0 0' }}>
        <Button
          variant='text'
          color='secondary'
          href={isPreview ? `${ADMINS_SMART_REPORTS_PATH}/${id}/edit` : ADMINS_SMART_REPORTS_PATH}
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: 'start' }}
        >
          <Typography variant='body1' sx={{ color: 'secondary' }}>
            {isPreview ? 'Back to editing' : 'Back to reports'}
          </Typography>
        </Button>
        <Grid container sx={{ display: 'flex', gap: '1rem' }}>
          {!isPreview && (
            <Button
              variant='text'
              color='secondary'
              href={`${ADMINS_SMART_REPORTS_PATH}/${id}/edit`}
              startIcon={<EditIcon />}
              sx={{ alignSelf: 'start' }}
            >
              <Typography variant='body1' sx={{ color: 'secondary' }}>
                Edit Report
              </Typography>
            </Button>
          )}
          <Button
            variant={'contained'}
            color='secondary'
            onClick={() => printDiv('SMART-REPORT-PRINT')}
            disabled
          >
            Print
          </Button>
        </Grid>
      </Grid>
      <Typography variant='h1' mb={2} mt={2}>{`${report.name} - ${entity?.name}`}</Typography>
      <Grid container spacing={2}>
        {report.smartReportVersions?.length > 0 &&
          report.smartReportVersions[0]?.pages?.map(pageData => (
            <ViewPage entity={entity} pageData={pageData} key={`page-${pageData.number}`} />
          ))}
      </Grid>
    </Box>
  );
};
